/**
 * CalibrationTakeList — renders the list of calibration takes with status badges.
 *
 * - Shows a spinner for in-progress takes.
 * - Shows an error message for failed takes.
 * - Polls status for takes that are still processing (via useCalibrationTakeStatus).
 * - Calls onDeleteTake when the coach clicks the remove button.
 * - Opens an inline CalibrationTakeEditForm when the coach clicks the edit
 *   button (take-edit-frontend) — declared lead side + failure-dimension
 *   tags. The row's own summary line (below) surfaces a declared/detected
 *   lead-side disagreement even while the editor is closed, since a
 *   misdetected take can otherwise sit unnoticed indefinitely.
 *
 * Each row carries `data-guide-section="calibration-{label}"` (calibration-upload-
 * guidance) so the TemplateGuideOverlay drawer follows the coach's scroll through
 * already-uploaded takes, reinforcing what makes that label's examples valuable.
 */

import { useState } from 'react';
import { PencilSquareIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import CalibrationTakeEditForm from './CalibrationTakeEditForm';
import TakeMeasureGoalsPanel from './TakeMeasureGoalsPanel';
import { CALIBRATION_REAL_FAILURE_DIMENSION_OPTIONS } from '@/utils/constants';
import { formatCalibrationGradeBadge } from '@/utils/gradeNumeric';
import { useCalibrationTakeStatus } from '@/hooks/useCalibrationTakes';
import type { CalibrationTake, CalibrationTakeStatus } from '@/types/calibrationTake';

// ---------------------------------------------------------------------------
// Status badge mapping
// ---------------------------------------------------------------------------

const STATUS_META: Record<
  CalibrationTakeStatus,
  { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }
> = {
  uploaded: { label: 'Queued', variant: 'default' },
  processing: { label: 'Processing', variant: 'warning' },
  completed: { label: 'Ready', variant: 'success' },
  failed: { label: 'Failed', variant: 'error' },
};

// Raw dimension key -> coach-facing label, sourced from the same options the
// upload form and the edit form offer (no second vocabulary to drift).
const DIMENSION_LABELS: Record<string, string> = Object.fromEntries(
  CALIBRATION_REAL_FAILURE_DIMENSION_OPTIONS.map((opt) => [opt.value, opt.label]),
);

function dimensionLabel(key: string): string {
  return DIMENSION_LABELS[key] ?? key;
}

/**
 * Resolve which failure-dimension tags apply to a take, mirroring the
 * backend's fallback order: `failure_dimensions` if non-empty, else
 * `[failure_dimension]` if set, else unscoped (no tags to show).
 */
function effectiveDimensions(take: CalibrationTake): string[] {
  if (take.failure_dimensions && take.failure_dimensions.length > 0) {
    return take.failure_dimensions;
  }
  if (take.failure_dimension) {
    return [take.failure_dimension];
  }
  return [];
}

function formatSide(side: string | null | undefined): string | null {
  if (side === 'left') return 'Left';
  if (side === 'right') return 'Right';
  return null;
}

/**
 * Collapsed-row lead-side summary. Renders nothing when neither a declared
 * nor a detected value exists. When a declared value disagrees with
 * detection, that's flagged (warning style) rather than silently showing
 * only one of the two — the disagreement is the reason this whole feature
 * exists (PR #393 shipped coach copy pointing at a fix that didn't exist
 * yet; this is the fix).
 */
function describeLeadSide(take: CalibrationTake): { text: string; isDisagreement: boolean } | null {
  const declared = formatSide(take.declared_lead_side);
  const detected = formatSide(take.detected_lead_side);
  if (!declared && !detected) return null;
  if (declared) {
    if (detected && detected !== declared) {
      return {
        text: `Lead side: ${declared} (you set this — detection read ${detected})`,
        isDisagreement: true,
      };
    }
    return { text: `Lead side: ${declared} (you set this)`, isDisagreement: false };
  }
  return { text: `Lead side: ${detected} (detected)`, isDisagreement: false };
}

// ---------------------------------------------------------------------------
// Single take row — polls while in-progress
// ---------------------------------------------------------------------------

interface TakeRowProps {
  take: CalibrationTake;
  templateId: string;
  onDelete: (takeId: string) => void;
  isDeleting: boolean;
}

function TakeRow({ take, templateId, onDelete, isDeleting }: TakeRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  // Goal Score PR-7b: the per-take "Goal measurements" panel (F §5, mockup
  // 04) is collapsed by default — measuring is an explicit, non-trivial
  // (recompute-fresh, never cached) backend action, not something to run
  // automatically every time the take list renders.
  const [showGoals, setShowGoals] = useState(false);

  // Poll status while not yet terminal
  const isTerminal = take.status === 'completed' || take.status === 'failed';
  const { data: polledTake } = useCalibrationTakeStatus(
    isTerminal ? undefined : templateId,
    isTerminal ? undefined : take.take_id,
  );

  // Use polled data when available to pick up the terminal state
  const displayTake = polledTake ?? take;
  const meta = STATUS_META[displayTake.status] ?? STATUS_META.uploaded;
  const isInProgress =
    displayTake.status === 'uploaded' || displayTake.status === 'processing';
  const dimensions = effectiveDimensions(displayTake);
  const leadSideSummary = describeLeadSide(displayTake);
  const gradeBadgeText = formatCalibrationGradeBadge(displayTake);

  return (
    <div
      className="flex items-start gap-3 p-3 rounded border border-border-subtle bg-dark-elevated-1"
      data-guide-section={`calibration-${displayTake.label}`}
    >
      {/* Status icon */}
      <div className="mt-0.5 flex-shrink-0">
        {isInProgress ? (
          <Spinner size="sm" />
        ) : displayTake.status === 'completed' ? (
          <span className="text-success text-base">&#10003;</span>
        ) : (
          <span className="text-danger text-base">&#215;</span>
        )}
      </div>

      {/* Take info */}
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-text-primary capitalize">
            {displayTake.label === 'good'
              ? 'Correct take'
              : displayTake.label === 'bad'
              ? 'Flawed take'
              : 'Disqualifying take'}
          </span>
          {dimensions.length > 0 && (
            <span className="text-xs text-text-muted">
              ({dimensions.map(dimensionLabel).join(', ')})
            </span>
          )}
          {/* Coach-assigned score-band grade (grade-feel scoring plan, Story
              4; numeric take-grade entry addendum) — shown so the grade set
              via the uploader/editor is actually visible afterward, not
              just write-only. "87 · B" when a numeric grade is set, plain
              "B" for a legacy letter-only take. */}
          {gradeBadgeText && (
            <Badge variant="secondary" size="sm" data-testid="take-grade-badge">
              Grade: {gradeBadgeText}
            </Badge>
          )}
          {/* Measured score (M) — the raw pipeline score this take earned in
              the calibration run, i.e. the number the grade scale anchors on.
              Surfaced 2026-09-01: an anchor-inversion refusal names two takes
              ("X measured worse than Y") but the coach previously had no way
              to see the measurements behind that claim. One decimal because
              inversions can be sub-point. */}
          {displayTake.status === 'completed' && displayTake.raw_match_score != null && (
            <Badge variant="secondary" size="sm" data-testid="take-mscore-badge">
              Measured: {displayTake.raw_match_score.toFixed(1)}
            </Badge>
          )}
          <Badge variant={meta.variant} size="sm">
            {meta.label}
          </Badge>
        </div>

        {/* Video filename — shown when the backend returns it (null-safe for older takes) */}
        {displayTake.original_filename && (
          <p className="text-xs text-text-muted truncate" title={displayTake.original_filename}>
            {displayTake.original_filename}
          </p>
        )}

        {/* Lead side — declared/detected summary. See describeLeadSide() docstring
            for why a disagreement is flagged rather than displayed neutrally. */}
        {leadSideSummary && (
          <p
            className={`text-xs ${leadSideSummary.isDisagreement ? 'text-warning font-medium' : 'text-text-muted'}`}
          >
            {leadSideSummary.text}
          </p>
        )}

        {/* Coach's reason for this take's label — coaching context, quoted + muted */}
        {displayTake.reason_text && (
          <p className="text-xs text-text-muted italic whitespace-pre-wrap break-words">
            &ldquo;{displayTake.reason_text}&rdquo;
          </p>
        )}

        {/* Error message for failed takes */}
        {displayTake.status === 'failed' && displayTake.error_message && (
          <p className="text-xs text-danger">{displayTake.error_message}</p>
        )}

        {/* Raw-match scoring problem on an otherwise-completed take (prod
            incident 2026-08-14): a take whose archived video is gone showed
            "completed" with no hint it could never anchor the scoring scale.
            raw_match_error is already coach-worded server-side; only shown
            when there is no successful score to stand behind (a stale score
            with a transient retry error is more useful than a warning). */}
        {displayTake.status === 'completed' &&
          displayTake.raw_match_error &&
          displayTake.raw_match_score == null && (
            <p className="text-xs text-warning" data-testid="take-raw-match-warning">
              {displayTake.raw_match_error}
            </p>
          )}

        {/* Non-blocking warnings */}
        {displayTake.validation_warnings.length > 0 && (
          <ul className="text-xs text-warning space-y-0.5 mt-1">
            {displayTake.validation_warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}

        {/* Processing status hint */}
        {isInProgress && (
          <p className="text-xs text-text-muted">
            Extracting pose data — this takes up to 60 seconds.
          </p>
        )}

        {/* Inline edit form — lead side + failure dimension tags */}
        {isEditing && (
          <CalibrationTakeEditForm
            templateId={templateId}
            take={displayTake}
            onClose={() => setIsEditing(false)}
          />
        )}

        {/* Goal Score PR-7b: per-take "Goal measurements" panel — below the
            existing grade/measured-score badges, a different purpose on
            the same object (F §5). Only shown for a completed take; an
            in-progress/failed take has no pose cache to measure against. */}
        {displayTake.status === 'completed' && showGoals && (
          <TakeMeasureGoalsPanel templateId={templateId} takeId={displayTake.take_id} />
        )}
      </div>

      {/* Edit + Delete buttons */}
      <div className="flex-shrink-0 flex items-center gap-1">
        {displayTake.status === 'completed' && (
          <button
            type="button"
            onClick={() => setShowGoals((v) => !v)}
            className="p-1 rounded text-text-muted hover:text-accent-indigo
              hover:bg-dark-elevated-2 transition-colors"
            aria-label={showGoals ? 'Hide Goal measurements' : 'Show Goal measurements'}
            aria-expanded={showGoals}
            title="Measure this take against this template's Goals"
            data-testid={`take-goals-toggle-${take.take_id}`}
          >
            <ChartBarIcon className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsEditing((v) => !v)}
          disabled={isDeleting}
          className="p-1 rounded text-text-muted hover:text-accent
            hover:bg-dark-elevated-2 transition-colors disabled:opacity-50"
          aria-label={isEditing ? 'Close take editor' : 'Edit take'}
          aria-expanded={isEditing}
          title="Edit lead side, mistake tags, or grade"
          data-testid={`edit-take-toggle-${take.take_id}`}
        >
          <PencilSquareIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(displayTake.take_id)}
          disabled={isDeleting || isEditing}
          className="p-1 rounded text-text-muted hover:text-danger
            hover:bg-dark-elevated-2 transition-colors disabled:opacity-50"
          aria-label="Remove take"
          title="Remove this take"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

interface CalibrationTakeListProps {
  templateId: string;
  takes: CalibrationTake[];
  onDeleteTake: (takeId: string) => void;
  isDeletingId: string | null;
}

export default function CalibrationTakeList({
  templateId,
  takes,
  onDeleteTake,
  isDeletingId,
}: CalibrationTakeListProps) {
  if (takes.length === 0) {
    return (
      <p className="text-sm text-text-muted text-center py-4">
        No calibration takes yet. Upload at least 2 correct takes to enable calibrated scoring.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {takes.map((take) => (
        <TakeRow
          key={take.take_id}
          take={take}
          templateId={templateId}
          onDelete={onDeleteTake}
          isDeleting={isDeletingId === take.take_id}
        />
      ))}
    </div>
  );
}
